/**
 * Catalog Bundle GraphQL Module
 * Fetches bundle metaobject data via GraphQL Storefront API for Catalog collection products
 */

(function() {
    'use strict';
    
    // Storefront API credentials
    const STOREFRONT_ACCESS_TOKEN = `f0aea6c9d0872231171fc827abb7071d`;
    const GRAPHQL_URL = `https://${Shopify.shop}/api/2024-01/graphql.json`;
    
    /**
     * Convert numeric product ID to Shopify GID format
     */
    function getProductGID(productId) {
        return `gid://shopify/Product/${productId}`;
    }
    
    /**
     * GraphQL query to fetch product metafield (to get metaobject GID)
     */
    function buildProductMetafieldQuery(productId) {
        const productGID = getProductGID(productId);
        
        return `
            query {
                product(id: "${productGID}") {
                    id
                    title
                    handle
                    variants(first: 1) {
                        edges {
                            node {
                                id
                                sku
                                availableForSale
                            }
                        }
                    }
                    bundleProducts: metafield(namespace: "custom", key: "bundle_products") {
                        id
                        type
                        value
                    }
                }
            }
        `;
    }
    
    /**
     * GraphQL query to fetch metaobject data directly using GID
     */
    function buildMetaobjectQuery(metaobjectGID) {
        return `
            query {
                metaobject(id: "${metaobjectGID}") {
                    id
                    type
                    name: field(key: "name") {
                        value
                    }
                    displayInputsAsRadioButtons: field(key: "display_inputs_as_radio_buttons") {
                        value
                    }
                    mainProduct: field(key: "product") {
                        reference {
                            ... on Product {
                                id
                                title
                            }
                        }
                    }
                    bundleOptions: field(key: "options") {
                        references(first: 20) {
                            edges {
                                node {
                                    ... on Metaobject {
                                        id
                                        type
                                        optionTitle: field(key: "title") {
                                            value
                                        }
                                        isRequired: field(key: "is_required") {
                                            value
                                        }
                                        makeDefault: field(key: "make_numbered_variant_default") {
                                            value
                                        }
                                        bundleItemQuantity: field(key: "bundle_item_quantity") {
                                            value
                                        }
                                        optionVariants: field(key: "variants") {
                                            references(first: 20) {
                                                edges {
                                                    node {
                                                        ... on ProductVariant {
                                                            id
                                                            title
                                                            sku
                                                            price {
                                                                amount
                                                            }
                                                            availableForSale
                                                            product {
                                                                id
                                                                title
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;
    }
    
    /**
     * Transform GraphQL response to match expected bundle data structure
     */
    function transformBundleData(productData, metaobjectData, productId) {
        try {
            if (!productData) {
                return null;
            }
            
            const bundleMetafield = productData.bundleProducts;
            if (!bundleMetafield || !bundleMetafield.value) {
                return null;
            }
            
            if (!metaobjectData || !metaobjectData.data?.metaobject) {
                return null;
            }
            
            const bundleMetaobject = metaobjectData.data.metaobject;
            const productTitle = productData.title || 'Engine Rebuild Kit';
            
            const bundleData = {
                product_id: productId,
                bundle_name: bundleMetaobject.name?.value || null,
                bundle_metaobject_id: bundleMetaobject.id,
                product: productId,
                main_product_title: productTitle,
                option_type: false,
                bundle_options: [{
                    options: []
                }]
            };
            
            const displayAsRadio = bundleMetaobject.displayInputsAsRadioButtons?.value;
            bundleData.option_type = displayAsRadio === 'true' || displayAsRadio === true || displayAsRadio === '1';
            
            const mainProductRef = bundleMetaobject.mainProduct?.reference;
            if (mainProductRef) {
                bundleData.product = parseInt(mainProductRef.id.split('/').pop());
                bundleData.main_product_title = mainProductRef.title || bundleData.main_product_title;
            }
            
            const optionsRefs = bundleMetaobject.bundleOptions?.references?.edges || [];
            
            optionsRefs.forEach((optionEdge, optionIndex) => {
                const optionMetaobject = optionEdge.node;
                if (!optionMetaobject) {
                    return;
                }
                
                const optionTitle = optionMetaobject.optionTitle?.value || null;
                const isRequired = optionMetaobject.isRequired?.value === 'true' || 
                                 optionMetaobject.isRequired?.value === true ||
                                 optionMetaobject.isRequired?.value === '1';
                const makeDefault = optionMetaobject.makeDefault?.value || null;
                const bundleItemQuantityRaw = optionMetaobject.bundleItemQuantity?.value;
                const optionQty = (bundleItemQuantityRaw != null && bundleItemQuantityRaw !== '') 
                    ? (parseInt(bundleItemQuantityRaw, 10) || 1) 
                    : 1;
                const variantRefs = optionMetaobject.optionVariants?.references?.edges || [];
                
                // Store the original title (with SKU suffix if present)
                const originalOptionTitle = optionTitle || `Option ${optionIndex + 1}`;
                
                // Clean name for display/matching (without SKU suffix)
                let optionName = originalOptionTitle;
                if (optionName && optionName.includes('|')) {
                    optionName = optionName.split('|')[0].trim();
                }
                
                const bundleOption = {
                    bundle_option_id: optionIndex + 1,
                    bundle_option_set_id: optionIndex + 1,
                    bundle_option_name: optionName,
                    bundle_option_original_name: originalOptionTitle, // Store original with SKU suffix
                    bundle_option_metaobject_id: optionMetaobject.id,
                    is_required: isRequired ? 1 : 0,
                    type: 'select',
                    options_values: []
                };
                
                variantRefs.forEach((variantEdge, variantIndex) => {
                    const variant = variantEdge?.node;
                    if (!variant) {
                        return;
                    }
                    
                    if (variant.__typename && variant.__typename !== 'ProductVariant') {
                        return;
                    }
                    
                    if (!variant.id || !variant.id.includes('ProductVariant')) {
                        return;
                    }
                    
                    const variantId = parseInt(variant.id.split('/').pop());
                    const variantProductId = variant.product ? parseInt(variant.product.id.split('/').pop()) : null;
                    const variantTitle = variant.title || 'Default Title';
                    const variantSku = variant.sku || '';
                    const variantPrice = variant.price?.amount || '0.00';
                    const isAvailable = variant.availableForSale || false;
                    
                    let isDefault = 0;
                    // Only set default if makeDefault is explicitly set and > 0
                    // For visible options, we don't want any default unless explicitly set
                    if (makeDefault) {
                        const defaultNum = parseInt(makeDefault) || 0;
                        if (defaultNum > 0 && variantIndex + 1 === defaultNum) {
                            isDefault = 1;
                        }
                        // Note: We don't set default for variantIndex === 0 if makeDefault is 0 or not set
                    }
                    // Removed: else if (variantIndex === 0) { isDefault = 1; }
                    // This ensures no default is set unless makeDefault is explicitly set and > 0
                    
                    let displayValue = variant.product?.title || variantTitle;
                    if (!displayValue || displayValue === 'Default Title') {
                        displayValue = variantSku || `Option ${variantIndex + 1}`;
                    }
                    
                    bundleOption.options_values.push({
                        option_value: displayValue,
                        varient_id: variantId,
                        product_id: variantProductId,
                        handle: variantSku,
                        price: variantPrice,
                        qty: String(optionQty),
                        is_default: isDefault,
                        available: isAvailable
                    });
                });
                
                bundleData.bundle_options[0].options.push(bundleOption);
            });
            
            bundleData.bundle_options[0].options.sort((a, b) => a.bundle_option_id - b.bundle_option_id);
            
            return bundleData;
            
        } catch (error) {
            console.error('[Catalog Bundle GraphQL] Error transforming bundle data:', error);
            return null;
        }
    }
    
    /**
     * Fetch bundle metaobject data for a single product
     */
    async function fetchBundleForProduct(productId) {
        try {
            // Step 1: Fetch product metafield to get metaobject GID
            const productQuery = buildProductMetafieldQuery(productId);
            
            const productResponse = await fetch(GRAPHQL_URL, {
                method: 'POST',
                headers: {
                    'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ query: productQuery })
            });
            
            if (!productResponse.ok) {
                throw new Error(`HTTP error! status: ${productResponse.status}`);
            }
            
            const productResult = await productResponse.json();
            
            if (productResult.errors) {
                console.error('[Catalog Bundle GraphQL] GraphQL errors:', productResult.errors);
                return null;
            }
            
            const product = productResult.data?.product;
            if (!product) {
                return null;
            }
            
            const bundleMetafield = product.bundleProducts;
            if (!bundleMetafield || !bundleMetafield.value) {
                return null;
            }
            
            // Step 2: Fetch metaobject data directly using GID
            const metaobjectGID = bundleMetafield.value;
            const metaobjectQuery = buildMetaobjectQuery(metaobjectGID);
            
            const metaobjectResponse = await fetch(GRAPHQL_URL, {
                method: 'POST',
                headers: {
                    'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ query: metaobjectQuery })
            });
            
            if (!metaobjectResponse.ok) {
                throw new Error(`HTTP error! status: ${metaobjectResponse.status}`);
            }
            
            const metaobjectResult = await metaobjectResponse.json();
            
            if (metaobjectResult.errors) {
                console.error('[Catalog Bundle GraphQL] GraphQL errors:', metaobjectResult.errors);
                return null;
            }
            
            // Transform the data
            const bundleData = transformBundleData(product, metaobjectResult, productId);
            
            return bundleData;
            
        } catch (error) {
            console.error('[Catalog Bundle GraphQL] Error fetching bundle data:', error);
            return null;
        }
    }
    
    /**
     * Fetch bundles for multiple products (batch processing)
     */
    async function fetchBundlesForProducts(products) {
        if (!products || products.length === 0) {
            return {};
        }
        
        // Initialize bundle data storage
        if (!window.catalogBundleData) {
            window.catalogBundleData = {};
        }
        
        
        const bundlePromises = products.map(async (product) => {
            const productId = product.product_id || product.id;
            if (!productId) {
                console.warn('[Catalog Bundle GraphQL] Product missing ID:', product);
                return null;
            }
            
            // Check if already fetched
            if (window.catalogBundleData[productId]) {
                return { productId, bundleData: window.catalogBundleData[productId] };
            }
            
            try {
                const bundleData = await fetchBundleForProduct(productId);
                if (bundleData) {
                    window.catalogBundleData[productId] = bundleData;
                    return { productId, bundleData };
                } else {
                }
            } catch (error) {
                console.error(`[Catalog Bundle GraphQL] Error fetching bundle for product ${productId}:`, error);
            }
            
            return null;
        });
        
        const results = await Promise.all(bundlePromises);
        
        // Filter out null results
        const validResults = results.filter(r => r !== null);
        
        
        return window.catalogBundleData;
    }
    
    // Export module
    window.catalogBundleGraphQL = {
        fetchBundleForProduct,
        fetchBundlesForProducts
    };
    
    
})();
